import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";

import { FilterChip, MobileFilterFold, MobileFilterFoldHandle } from "/imports/ui/components/booking";
import ClassDayStrip from "./ClassDayStrip";

// Mobile: collapsible filter chips, then the week day-strip. Only the day
// strip is sticky (bleeding to the container edges, directly under the 64px app
// bar): the filters are ordinary page content, so once the client scrolls into
// the classes they scroll away instead of holding half the screen. They fold
// behind a summary row from the top, or from the handle under the last filter.
function ClassMobileFilterBar({ cls, scrollSpy }) {
  const {
    navigate,
    slug,
    filters,
    setActiveSheet,
    availableAges,
    levelFilterAvailable,
    levelScope,
    levelChipLabel,
    isAuthenticated,
    canApplyPreferences,
    applyPreferences,
    selectedSport,
    selectedService,
    selectedTrainer,
    selectedLocationObj,
    selectedAge
  } = cls;
  const { stickyRef } = scrollSpy;
  const location = useLocation();

  // Always starts expanded — the filters decide what the list shows, and a
  // client who never sees them books the wrong thing. Folding is opt-in.
  const [filtersOpen, setFiltersOpen] = useState(true);

  const summary = [
    selectedSport?.name || "All sports",
    selectedService?.name,
    selectedTrainer?.name,
    availableAges.length > 1 ? selectedAge?.label : null,
    levelFilterAvailable && levelScope === "mine" ? levelChipLabel : null,
    selectedLocationObj?.name
  ]
    .filter(Boolean)
    .join(" · ");

  const goEditPreferences = () => navigate(`/${slug}/dashboard?tab=preferences&from=${encodeURIComponent(location.pathname + location.search)}`);

  return (
    <>
      <MobileFilterFold open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)} summary={summary} />

      {filtersOpen && (
        <>
          {isAuthenticated && (
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {canApplyPreferences ? (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: "1rem" }} />}
                    onClick={applyPreferences}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                  >
                    Use my preferences
                  </Button>
                  <IconButton
                    size="small"
                    onClick={goEditPreferences}
                    aria-label="Update preferences"
                    sx={{ flexShrink: 0, border: "1px solid", borderColor: "primary.main", borderRadius: 2, color: "primary.main", px: 1 }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<TuneIcon sx={{ fontSize: "1rem" }} />}
                  onClick={goEditPreferences}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  Set my preferences
                </Button>
              )}
            </Box>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
            <FilterChip label="Sport" value={selectedSport?.name || "All"} active={!!filters.sport} onClick={() => setActiveSheet("sport")} />
            <FilterChip
              label="Location"
              value={selectedLocationObj?.name || "All"}
              active={!!filters.locationId}
              onClick={() => setActiveSheet("location")}
            />
            <FilterChip label="Class" value={selectedService?.name || "All"} active={!!filters.serviceId} onClick={() => setActiveSheet("service")} />
            <FilterChip
              label="Trainer"
              value={selectedTrainer?.name || "All"}
              active={!!filters.trainerId}
              onClick={() => setActiveSheet("trainer")}
            />
            {levelFilterAvailable && (
              // Full width: the value pairs a sport with a level name ("Tennis ·
              // Advanced"), which a half-width chip truncates.
              <Box sx={{ gridColumn: "1 / -1" }}>
                <FilterChip
                  label="Level"
                  value={levelScope === "mine" ? levelChipLabel : "All"}
                  active={levelScope === "mine"}
                  onClick={() => setActiveSheet("level")}
                />
              </Box>
            )}
            {availableAges.length > 1 && (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <FilterChip
                  label="Age & Gender"
                  value={selectedAge?.label || "No preference"}
                  active={!!filters.age}
                  onClick={() => setActiveSheet("age")}
                />
              </Box>
            )}
          </Box>
          <MobileFilterFoldHandle onFold={() => setFiltersOpen(false)} />
        </>
      )}

      <Box
        ref={stickyRef}
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 5,
          mx: -2,
          px: 2,
          pt: 1.5,
          pb: 1.25,
          bgcolor: "background.default",
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <ClassDayStrip cls={cls} scrollSpy={scrollSpy} />
      </Box>
    </>
  );
}

export default ClassMobileFilterBar;
